import { Suspense, useCallback, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useParams } from 'react-router-dom';

import { Splitter } from 'antd';

import { NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import { removeCategoryPrefix } from '@/shared/lib/format/fqn';

import { ClassificationFormModal } from '@/features/tags/ui/ClassificationFormModal';
import { TagCategoryTree } from '@/features/tags/ui/TagCategoryTree';

import { TagContents } from '@/widgets/tags/TagContents/TagContents';

import styles from './TagsPage.module.css';

const SPLITTER_SIZES = {
  DEFAULT: 300,
  MIN: 250,
  MAX: 500,
} as const;

export const TagsPage = () => {
  const { tagCategory: selectedTagCategory, tagFqn: tagFqn } = useParams<{
    tagCategory?: string;
    tagFqn?: string;
  }>();
  const selectedTagName = removeCategoryPrefix(tagFqn ?? '');
  const selectedTagFqn =
    selectedTagCategory && tagFqn ? `${selectedTagCategory}.${selectedTagName}` : selectedTagCategory;

  const [isClassificationModalOpen, setIsClassificationModalOpen] = useState(false);
  const [refreshParentFqn, setRefreshParentFqn] = useState<string | null>(null);

  const handleAddClassificationClick = useCallback(() => {
    setIsClassificationModalOpen(true);
  }, []);

  const handleClassificationModalClose = useCallback(() => {
    setIsClassificationModalOpen(false);
  }, []);

  const handleClassificationSuccess = useCallback((parentFqn: string) => {
    setRefreshParentFqn(parentFqn || '');
  }, []);

  const handleTreeRefresh = useCallback((parentFqn: string) => {
    setRefreshParentFqn(parentFqn);
  }, []);

  const handleRefreshComplete = useCallback(() => {
    setRefreshParentFqn(null);
  }, []);

  return (
    <div className={styles.tagsPage}>
      <Splitter className={styles.splitter}>
        <Splitter.Panel
          defaultSize={SPLITTER_SIZES.DEFAULT}
          min={SPLITTER_SIZES.MIN}
          max={SPLITTER_SIZES.MAX}
          resizable>
          <ErrorBoundary
            FallbackComponent={(props) => (
              <TagCategoryTree.Error
                {...props}
                title={NOTIFICATION_MESSAGES.TAGS_COMPONENT_ERROR}
                description={NOTIFICATION_MESSAGES.TAGS_COMPONENT_ERROR_DESC}
              />
            )}>
            <TagCategoryTree.Tree
              selectedTagFqn={selectedTagFqn}
              onAddClick={handleAddClassificationClick}
              refreshParentFqn={refreshParentFqn}
              onRefreshComplete={handleRefreshComplete}
            />
          </ErrorBoundary>
        </Splitter.Panel>

        <Splitter.Panel>
          <ErrorBoundary
            FallbackComponent={(props) => (
              <TagContents.Error
                {...props}
                title={NOTIFICATION_MESSAGES.TAGS_COMPONENT_ERROR}
                description={NOTIFICATION_MESSAGES.TAGS_COMPONENT_ERROR_DESC}
              />
            )}>
            <Suspense fallback={<TagContents.Fallback />}>
              {selectedTagCategory && (
                <TagContents.Main
                  selectedTagCategory={selectedTagCategory}
                  selectedTagName={selectedTagName ?? ''}
                  selectedTagFqn={selectedTagFqn ?? ''}
                  onTreeRefresh={handleTreeRefresh}
                />
              )}
            </Suspense>
          </ErrorBoundary>
        </Splitter.Panel>
      </Splitter>

      <ClassificationFormModal
        open={isClassificationModalOpen}
        mode="create"
        onClose={handleClassificationModalClose}
        onSuccess={handleClassificationSuccess}
      />
    </div>
  );
};
